import { redirect, LoaderFunction } from "react-router";
import { store } from "@/redux";
import { userAuthorizedSelector } from "@/redux/authorization/authorization.selector";
import { selectUserProfile } from "@/redux/user/user.selector";
import {
  LOGOUT_REASON_MANUAL,
  consumeLogoutReason
} from "@/utils/logout-redirect";
import { debug } from "@/utils/debug";

const getRedirectToFromUrl = (url: URL) => {
  if (url.pathname.startsWith("/app/dashboard")) {
    return url.pathname;
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

export const sanitizeRedirectTo = (redirectTo: string | null) => {
  if (!redirectTo) return null;

  const hasAsciiControlChars = (() => {
    for (let i = 0; i < redirectTo.length; i++) {
      const code = redirectTo.charCodeAt(i);
      if (code < 32 || code === 127) return true;
    }
    return false;
  })();

  if (
    redirectTo.includes("\\") ||
    hasAsciiControlChars ||
    !redirectTo.startsWith("/") ||
    redirectTo.startsWith("//")
  ) {
    return null;
  }

  return redirectTo;
};

export const requireAuth: LoaderFunction = ({ request }) => {
  const state = store.getState();
  const { isAuthorized } = userAuthorizedSelector(state);

  if (!isAuthorized) {
    const logoutReason = consumeLogoutReason();
    if (logoutReason === LOGOUT_REASON_MANUAL) {
      throw redirect("/auth/login");
    }

    const url = new URL(request.url);
    const redirectTo = sanitizeRedirectTo(getRedirectToFromUrl(url));
    const redirectToQuery = redirectTo
      ? `?redirectTo=${encodeURIComponent(redirectTo)}`
      : "";

    debug.log("[loader] requireAuth: not authorized — redirecting to login");
    throw redirect(`/auth/login${redirectToQuery}`);
  }

  return null;
};

export const requireRoles = (roles: string[]): LoaderFunction => {
  return (): null => {
    const state = store.getState();
    const profile = selectUserProfile(state);

    if (profile && !roles.includes(`${profile?.role?.role_code}`)) {
      throw redirect("/app/home");
    }

    return null;
  };
};

export const redirectIfAuthenticated: LoaderFunction = ({ request }) => {
  const state = store.getState();
  const { isAuthorized } = userAuthorizedSelector(state);

  if (isAuthorized) {
    debug.log(
      "[loader] redirectIfAuthenticated: authorized — redirecting away from login"
    );
    const url = new URL(request.url);
    const redirectTo = sanitizeRedirectTo(url.searchParams.get("redirectTo"));
    throw redirect(redirectTo || "/app/home");
  }

  return null;
};

export const rootRedirect: LoaderFunction = () => {
  const state = store.getState();
  const { isAuthorized } = userAuthorizedSelector(state);

  if (isAuthorized) {
    throw redirect("/app/home");
  }
  throw redirect("/auth/login");
};

export const appIndexRedirect: LoaderFunction = () => {
  throw redirect("/app/home");
};
